import { attr, css, FASTElement, html, Observable } from '@microsoft/fast-element';
import { brand, designTokens } from '../../design-tokens';
import { display } from '../../foundation/display';
import { NatGenDesignSystem } from '../../natgen-design-system';
import { DesignToken, DesignTokenChangeRecord, DesignTokenSubscriber } from '../../foundation/design-token';
import { backgroundColorContext } from '../../context/background-context';

export class NatGenLogo extends FASTElement implements DesignTokenSubscriber<DesignToken<any>> {
    @attr
    brand: string;

    @attr
    src: string;

    @attr
    size: number;

    @attr
    padding: number;

    @attr 
    alt: string;

    @backgroundColorContext
    isDarkMode: boolean;

    connectedCallback(): void {
        super.connectedCallback();
        brand.subscribe(this);
    }

    handleChange(token: DesignToken<any>, record: DesignTokenChangeRecord<DesignToken<any>>): void {
        Observable.notify(this, 'logo');
    }

    disconnectedCallback(): void {
        super.disconnectedCallback();
        brand.unsubscribe(this);
    }

    getTokenName() : string {
        const currentBrand = this.brand || brand.getValueFor(this);
        let token = `branding-logos-${currentBrand}`

        if (this.isDarkMode && designTokens[`${token}-dark-image`])
            token += '-dark';

        return token;
    }

    getLogoPath() : string {
        if (this.src)
            return this.src;

        Observable.track(this, 'logo');

        return `${process.env.ASSET_PATH ? process.env.ASSET_PATH : ''}branding/${designTokens[`${this.getTokenName()}-image`]?.getValueFor(this)}`;
    }

    get logoDescription() : string {
        if (this.alt)
            return this.alt;

        Observable.track(this, 'logo');
        return designTokens[`${this.getTokenName()}-description`]?.getValueFor(this) || 'logo';
    }

    hasDefinedHeight() : boolean {
        if (this.size)
            return true;

        return `${this.getTokenName()}-size` in designTokens;
    }

    get logoHeight() : string {
        if (this.size)
            return `${this.size * designTokens['space-design-unit'].getValueFor(this)}px`

        Observable.track(this, 'logo');

        return this.hasDefinedHeight() ?
            `${(designTokens[`${this.getTokenName()}-size`]?.getValueFor(this) || 10) * designTokens['space-design-unit'].getValueFor(this)}px` : 'auto';
    }

    hasDefinedHeightSmall() : boolean {
        if (this.size)
            return true;

        return `${this.getTokenName()}-size-small` in designTokens;
    }

    get logoHeightSmall() : string {
        if (this.size)
            return `${this.size * designTokens['space-design-unit'].getValueFor(this)}px`

        Observable.track(this, 'logo');

        return this.hasDefinedHeightSmall() ?
            `${(designTokens[`${this.getTokenName()}-size-small`]?.getValueFor(this) || 10) * designTokens['space-design-unit'].getValueFor(this)}px` : this.logoHeight;
    }

    hasDefinedPadding() : boolean {
        if (this.padding)
            return true;

        Observable.track(this, 'logo');

        return `${this.getTokenName()}-padding` in designTokens;
    }

    get logoPadding() : string {
        if (this.padding)
            return `${this.padding * designTokens['space-design-unit'].getValueFor(this)}px`

        Observable.track(this, 'logo');

        return this.hasDefinedPadding() ?
            `${(designTokens[`${this.getTokenName()}-padding`]?.getValueFor(this) || 0) * designTokens['space-design-unit'].getValueFor(this)}px` : '0';
    }
}

export const natGenLogoDefinition = NatGenLogo.compose({
    name: `${NatGenDesignSystem.prefix}-logo`,
    template: html<NatGenLogo>`
        <img src="${x => x.getLogoPath()}" alt="${x => x.logoDescription}" />
    `,
    styles: css<NatGenLogo>`
        ${display('block')}

        :host img {
            height: ${x => x.logoHeight};
            margin: ${x => x.logoPadding};
        }

        @media (max-width: 640px) {
            :host img {
                height: ${x => x.logoHeightSmall};
            }
        }
    `
});
